import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { 
  Loader2, Download, Sparkles, Image as ImageIcon, Wand2, 
  Type, Upload, RotateCcw, Plus, Trash2,
  AlignLeft, AlignCenter, AlignRight, Bold, Italic, Eye, LayoutTemplate
} from 'lucide-react';
import { toast } from 'sonner';
import { buildGif } from '../utils/gifBuilder';
import { buildWhatsAppSticker, shareStickerOrDownload } from '../utils/stickerHelper';
import { ART_TEMPLATE_CATEGORIES, ART_TEMPLATES_FLAT } from '../data/artTemplates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Arts = () => {
  // Main states
  const [activeTab, setActiveTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [fileId, setFileId] = useState(null);
  
  // AI Generation states
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('default');
  const [aiQuality, setAiQuality] = useState('high'); // 'high' = Nano Banana Pro, 'fast' = Nano Banana Flash
  const [aiVariations, setAiVariations] = useState(1);
  const [aiAspect, setAiAspect] = useState('1:1');
  const [variationResults, setVariationResults] = useState([]);
  // Background replacement (upload image + new background prompt)
  const [bgImageFile, setBgImageFile] = useState(null);
  const [bgImagePreview, setBgImagePreview] = useState(null);
  const [bgPrompt, setBgPrompt] = useState('');
  const [bgQuality, setBgQuality] = useState('high'); // Default Pro (works reliably with async polling)
  const [bgResult, setBgResult] = useState(null);
  const bgFileInputRef = useRef(null);

  // GIF Animado tab
  const [gifEffect, setGifEffect] = useState('text');
  const [gifText, setGifText] = useState('');
  const [gifTextColor, setGifTextColor] = useState('#FFFFFF');
  const [gifStrokeColor, setGifStrokeColor] = useState('#000000');
  const [gifBgColor, setGifBgColor] = useState('#FFFFFF');
  const [gifSize, setGifSize] = useState(320);
  const [gifFrames, setGifFrames] = useState(30);
  const [gifSticker, setGifSticker] = useState(false);
  const [gifResult, setGifResult] = useState(null); // object URL
  const [gifProgress, setGifProgress] = useState(0);
  const [gifGenerating, setGifGenerating] = useState(false);
  
  // Background image states
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [originalImage, setOriginalImage] = useState(null); // Keep original
  
  // Text overlay states
  const [textLayers, setTextLayers] = useState([]);
  const [selectedLayerId, setSelectedLayerId] = useState(null);
  
  // Canvas states
  const [canvasSize, setCanvasSize] = useState({ width: 1080, height: 1080 });
  const [aspectRatio, setAspectRatio] = useState('square'); // square, portrait, landscape, sublimacion
  const [fitMode, setFitMode] = useState('contain'); // contain or cover
  
  // Sublimacion custom size in centimeters
  const [customSizeCm, setCustomSizeCm] = useState({ width: 20, height: 20 });
  const [printDpi, setPrintDpi] = useState(300); // DPI for sublimation printing
  const [paperSize, setPaperSize] = useState('custom'); // Predefined paper sizes
  
  // Canvas ref
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewRef = useRef(null);

  // Convert cm to pixels at given DPI
  const cmToPixels = (cm, dpi) => Math.round((cm / 2.54) * dpi);

  // Predefined paper sizes in cm (width x height)
  const paperSizes = [
    { value: 'custom', label: '📐 Personalizado', width: null, height: null },
    { value: 'carta', label: '📄 Carta (21.6 × 27.9 cm)', width: 21.6, height: 27.9 },
    { value: 'carta_landscape', label: '📄 Carta Horizontal (27.9 × 21.6 cm)', width: 27.9, height: 21.6 },
    { value: 'oficio', label: '📋 Oficio (21.6 × 35.6 cm)', width: 21.6, height: 35.6 },
    { value: 'oficio_landscape', label: '📋 Oficio Horizontal (35.6 × 21.6 cm)', width: 35.6, height: 21.6 },
    { value: 'a4', label: '📃 A4 (21 × 29.7 cm)', width: 21, height: 29.7 },
    { value: 'a4_landscape', label: '📃 A4 Horizontal (29.7 × 21 cm)', width: 29.7, height: 21 },
    { value: 'a3', label: '📰 A3 (29.7 × 42 cm)', width: 29.7, height: 42 },
    { value: 'a3_landscape', label: '📰 A3 Horizontal (42 × 29.7 cm)', width: 42, height: 29.7 },
    { value: 'tabloide', label: '🗞️ Tabloide (27.9 × 43.2 cm)', width: 27.9, height: 43.2 },
    { value: 'tabloide_landscape', label: '🗞️ Tabloide Horizontal (43.2 × 27.9 cm)', width: 43.2, height: 27.9 },
    { value: 'taza_11oz', label: '☕ Taza 11oz (9.5 × 20 cm)', width: 9.5, height: 20 },
    { value: 'taza_15oz', label: '☕ Taza 15oz (10 × 22 cm)', width: 10, height: 22 },
    { value: 'cojin_40', label: '🛋️ Cojín 40×40 cm', width: 40, height: 40 },
    { value: 'cojin_45', label: '🛋️ Cojín 45×45 cm', width: 45, height: 45 },
    { value: 'mousepad', label: '🖱️ Mousepad (24 × 20 cm)', width: 24, height: 20 },
  ];

  // Aspect ratio options
  const aspectRatios = [
    { value: 'square', label: '⬜ Cuadrado (1:1)', width: 1080, height: 1080 },
    { value: 'portrait', label: '📱 Vertical (9:16)', width: 1080, height: 1920 },
    { value: 'landscape', label: '🖼️ Horizontal (16:9)', width: 1920, height: 1080 },
    { value: 'whatsapp', label: '💬 WhatsApp Status', width: 1080, height: 1920 },
    { value: 'post', label: '📷 Post (4:5)', width: 1080, height: 1350 },
    { value: 'sublimacion', label: '🎨 Sublimación (cm)', width: null, height: null },
  ];

  // Update canvas size when aspect ratio changes
  const handleAspectRatioChange = (value) => {
    setAspectRatio(value);
    if (value === 'sublimacion') {
      // For sublimation, calculate pixels from cm at specified DPI
      const widthPx = cmToPixels(customSizeCm.width, printDpi);
      const heightPx = cmToPixels(customSizeCm.height, printDpi);
      setCanvasSize({ width: widthPx, height: heightPx });
    } else {
      const selected = aspectRatios.find(a => a.value === value);
      if (selected && selected.width) {
        setCanvasSize({ width: selected.width, height: selected.height });
      }
    }
  };

  // Update sublimation size when cm values change
  const handleSublimationSizeChange = (dimension, value) => {
    const newSize = { ...customSizeCm, [dimension]: parseFloat(value) || 1 };
    setCustomSizeCm(newSize);
    setPaperSize('custom'); // Reset to custom when manually editing
    if (aspectRatio === 'sublimacion') {
      const widthPx = cmToPixels(newSize.width, printDpi);
      const heightPx = cmToPixels(newSize.height, printDpi);
      setCanvasSize({ width: widthPx, height: heightPx });
    }
  };

  // Handle paper size selection
  const handlePaperSizeChange = (value) => {
    setPaperSize(value);
    const selected = paperSizes.find(p => p.value === value);
    if (selected && selected.width && selected.height) {
      setCustomSizeCm({ width: selected.width, height: selected.height });
      if (aspectRatio === 'sublimacion') {
        const widthPx = cmToPixels(selected.width, printDpi);
        const heightPx = cmToPixels(selected.height, printDpi);
        setCanvasSize({ width: widthPx, height: heightPx });
      }
    }
  };

  // Update DPI for sublimation
  const handleDpiChange = (value) => {
    const dpi = parseInt(value) || 300;
    setPrintDpi(dpi);
    if (aspectRatio === 'sublimacion') {
      const widthPx = cmToPixels(customSizeCm.width, dpi);
      const heightPx = cmToPixels(customSizeCm.height, dpi);
      setCanvasSize({ width: widthPx, height: heightPx });
    }
  };

  const styles = [
    { value: 'default', label: '🎨 Por defecto' },
    { value: 'sublimacion', label: '👕 Sublimación' },
    { value: 'social', label: '📱 Redes Sociales' },
    { value: 'whatsapp', label: '💬 Estado WhatsApp' },
    { value: 'print', label: '🖨️ Impresión' },
    { value: 'artistico', label: '🖼️ Artístico' },
    { value: 'realista', label: '📸 Realista' },
    { value: 'cartoon', label: '🎬 Cartoon' },
    { value: 'neon', label: '✨ Neón/Brillante' },
    { value: 'vintage', label: '📻 Vintage Retro' },
    { value: 'watercolor', label: '🎨 Acuarela' },
    { value: '3d', label: '🟦 3D Render' },
    { value: 'minimal', label: '⬜ Minimalista' },
  ];

  const presetColors = [
    '#FFFFFF', '#000000', '#FF0000', '#00FF00', '#0000FF', 
    '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080',
    '#FFD700', '#FF69B4', '#4169E1', '#32CD32', '#DC143C'
  ];

  // Presets rápidos de fondos para "Reemplazar Fondo con IA"
  const bgPresets = [
    { label: '🎂 Cumpleaños niña', prompt: 'Escenografía de cumpleaños para niña: globos rosados y dorados, flores rosadas, oso de peluche, luces cálidas tipo bokeh, alfombra de peluche rosa, marco decorativo dorado' },
    { label: '🎈 Cumpleaños niño', prompt: 'Escenografía de cumpleaños para niño: globos azules y dorados, estrellas, confeti, luces cálidas tipo bokeh, alfombra azul, decoración elegante' },
    { label: '👶 Felices X Meses', prompt: 'Sesión de mes-versario de bebé: fondo crema con globos pastel, flores rosadas y blancas, oso de peluche, luces tipo bokeh dorado, alfombra de peluche, ambiente de estudio profesional' },
    { label: '🎄 Navidad', prompt: 'Escenografía navideña: árbol de Navidad iluminado, regalos envueltos, luces cálidas, copos de nieve, mantel rojo, ambiente cozy de invierno' },
    { label: '🎃 Halloween', prompt: 'Escenografía de Halloween: calabazas talladas, telarañas, hojas otoñales, niebla suave, luces anaranjadas, fondo oscuro misterioso' },
    { label: '💖 Día de la madre', prompt: 'Escenografía día de la madre: ramo de rosas rosadas, corazones, luces cálidas tipo bokeh, fondo color durazno suave, ambiente elegante' },
    { label: '🏖️ Playa tropical', prompt: 'Playa tropical al atardecer: arena dorada, palmeras, mar turquesa al fondo, cielo con tonos naranja y rosa, ambiente cálido y relajado' },
    { label: '🌸 Jardín de flores', prompt: 'Jardín de flores hermoso: rosas, tulipanes y peonías en tonos pastel, luz natural suave del amanecer, mariposas, ambiente romántico' },
    { label: '⚪ Estudio blanco', prompt: 'Fondo de estudio profesional blanco infinito, iluminación suave y uniforme tipo softbox, ideal para producto, sin sombras duras, fondo limpio sin elementos' },
    { label: '⚫ Estudio negro', prompt: 'Fondo de estudio profesional negro liso, iluminación dramática lateral, ideal para producto premium, sin elementos distractores' },
    { label: '🏙️ Ciudad nocturna', prompt: 'Ciudad de noche con luces de neón coloridas, edificios iluminados al fondo desenfocado tipo bokeh, ambiente urbano moderno y vibrante' },
    { label: '🌿 Naturaleza verde', prompt: 'Naturaleza exuberante: bosque con hojas verdes desenfocadas tipo bokeh, rayos de sol filtrándose entre los árboles, ambiente fresco y natural' },
  ];

  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Pacifico&family=Lobster&family=Dancing+Script&family=Satisfy&family=Great+Vibes&family=Playfair+Display&family=Oswald&family=Bebas+Neue&family=Permanent+Marker&family=Shadows+Into+Light&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);

  // Add new text layer
  const addTextLayer = () => {
    const newLayer = {
      id: Date.now(),
      text: 'Tu texto aquí',
      font: 'Arial',
      size: 48,
      color: '#FFFFFF',
      strokeColor: '#000000',
      strokeWidth: 2,
      x: 50,
      y: 50,
      bold: false,
      italic: false,
      align: 'center',
      shadow: true,
      maxWidth: 80 // percentage of canvas width
    };
    setTextLayers([...textLayers, newLayer]);
    setSelectedLayerId(newLayer.id);
    setActiveTab('text'); // Switch to text tab
  };

  // Update text layer
  const updateTextLayer = (id, updates) => {
    setTextLayers(textLayers.map(layer => 
      layer.id === id ? { ...layer, ...updates } : layer
    ));
  };

  // Delete text layer
  const deleteTextLayer = (id) => {
    setTextLayers(textLayers.filter(layer => layer.id !== id));
    if (selectedLayerId === id) setSelectedLayerId(null);
  };

  // Cargar una plantilla prediseñada: fondo + capas de texto editables
  const applyTemplate = (tpl) => {
    setAspectRatio('square');
    setFitMode('cover');
    setBackgroundImage(tpl.bg);
    setOriginalImage(tpl.bg);
    const layers = (tpl.layers || []).map((l, i) => ({ ...l, id: Date.now() + i }));
    setTextLayers(layers);
    setSelectedLayerId(layers.length ? layers[0].id : null);
    setActiveTab('text');
    toast.success(`Plantilla "${tpl.name}" cargada. Edita los textos y descarga.`);
  };

  // Handle background image upload
  const handleBackgroundUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBackgroundFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setBackgroundImage(e.target.result);
        setOriginalImage(e.target.result); // Keep a copy of original
      };
      reader.readAsDataURL(file);
      toast.success('Imagen cargada. Ahora puedes agregar texto encima.');
    }
  };

  // Generate AI art
  const generateArt = async () => {
    if (!prompt.trim()) {
      toast.error('Por favor escribe una descripción');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/arts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          style,
          quality: aiQuality,
          variations: aiVariations,
          aspect_ratio: aiAspect,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setBackgroundImage(data.image_base64);
        setOriginalImage(data.image_base64);
        setFileId(data.file_id);
        setVariationResults(data.variations || []);
        const modelName = data.model?.includes('pro') ? 'Pro' : 'Flash';
        toast.success(`¡Arte generado con Gemini ${modelName}!${(data.variations || []).length > 1 ? ` (${data.variations.length} variantes)` : ''}`);
      } else {
        toast.error(data.detail || 'Error al generar arte');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Handle upload for "Reemplazar Fondo" tab (with auto-resize for large images)
  const handleBgImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('El archivo debe ser una imagen');
      return;
    }

    const MAX_SIDE = 1600; // px max side, keeps quality but avoids 8MB+ uploads/timeouts
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let w = img.width;
        let h = img.height;
        const longest = Math.max(w, h);
        if (longest > MAX_SIDE) {
          const scale = MAX_SIDE / longest;
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          if (!blob) {
            toast.error('No se pudo procesar la imagen');
            return;
          }
          const resizedFile = new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
          setBgImageFile(resizedFile);
          setBgImagePreview(canvas.toDataURL('image/jpeg', 0.92));
          setBgResult(null);
        }, 'image/jpeg', 0.92);
      };
      img.onerror = () => toast.error('No se pudo leer la imagen');
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Convert a base64 dataURL to a File (used when image came from another tab)
  const dataUrlToFile = async (dataUrl, filename = 'image.jpg') => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || 'image/jpeg' });
  };

  // Replace background using Gemini Nano Banana (image-to-image, ASYNC mode)
  const handleReplaceBackground = async () => {
    // Pick the best image source: 1) this tab upload, 2) backgroundFile from "Imagen" tab, 3) current backgroundImage dataURL (incl. AI-generated)
    let imageToSend = bgImageFile || backgroundFile || null;
    if (!imageToSend && (originalImage || backgroundImage)) {
      try {
        imageToSend = await dataUrlToFile(originalImage || backgroundImage, 'editor-image.jpg');
      } catch (_e) {
        imageToSend = null;
      }
    }
    if (!imageToSend) {
      toast.error('Sube una imagen primero (pestaña "Cambiar Fondo" o "Imagen")');
      return;
    }
    if (!bgPrompt.trim()) {
      toast.error('Describe el nuevo fondo que quieres');
      return;
    }

    setLoading(true);
    setBgResult(null);
    const loadingToast = toast.loading('Procesando con IA… esto puede tardar 20-50 segundos', { duration: Infinity });

    try {
      const formData = new FormData();
      formData.append('image', imageToSend);
      formData.append('prompt', bgPrompt.trim());
      formData.append('style', style);
      formData.append('quality', bgQuality);
      formData.append('mode', 'replace_background');

      // 1) Submit job (returns immediately with job_id)
      const submitResp = await fetch(`${API_URL}/api/arts/modify-async`, {
        method: 'POST',
        body: formData,
      });

      const submitRawText = await submitResp.text();
      let submitData = null;
      try { submitData = submitRawText ? JSON.parse(submitRawText) : null; } catch (_e) { submitData = null; }

      if (!submitResp.ok || !submitData?.job_id) {
        toast.dismiss(loadingToast);
        toast.error(submitData?.detail || 'No se pudo iniciar el procesamiento');
        return;
      }

      const jobId = submitData.job_id;

      // 2) Poll every 3s, max 3 minutes
      const POLL_INTERVAL_MS = 3000;
      const MAX_POLLS = 60;
      let finalData = null;

      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        try {
          const poll = await fetch(`${API_URL}/api/arts/job/${jobId}`);
          const pollText = await poll.text();
          let pollData = null;
          try { pollData = pollText ? JSON.parse(pollText) : null; } catch (_e) { pollData = null; }
          if (pollData?.status === 'done') {
            finalData = pollData.result;
            break;
          }
          if (pollData?.status === 'error') {
            toast.dismiss(loadingToast);
            toast.error(pollData.error || 'La IA no pudo procesar la imagen. Intenta con otra descripción.');
            return;
          }
        } catch (_pollErr) {
          // network blip, keep polling
        }
      }

      toast.dismiss(loadingToast);

      if (finalData && finalData.success) {
        setBgResult(finalData.image_base64);
        setFileId(finalData.file_id);
        const modelName = finalData.model?.includes('pro') ? 'Pro' : 'Flash';
        toast.success(`¡Fondo reemplazado con Gemini ${modelName}!`);
      } else {
        toast.error('La IA tardó demasiado. Intenta de nuevo o usa Flash.', { duration: 8000 });
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Error de conexión: ' + (error.message || 'desconocido'));
    } finally {
      setLoading(false);
    }
  };

  // Use the AI-modified image as background of the main editor
  const useBgResultAsBackground = () => {
    if (!bgResult) return;
    setBackgroundImage(bgResult);
    setOriginalImage(bgResult);
    setActiveTab('text');
    toast.success('Imagen lista. Agrega texto si lo deseas.');
  };

  // Isolate subject: remove all surrounding objects, keep only the person/baby/animal
  const handleIsolateSubject = async () => {
    let imageToSend = bgImageFile || backgroundFile || null;
    if (!imageToSend && (originalImage || backgroundImage)) {
      try {
        imageToSend = await dataUrlToFile(originalImage || backgroundImage, 'editor-image.jpg');
      } catch (_e) {
        imageToSend = null;
      }
    }
    if (!imageToSend) {
      toast.error('Sube una imagen primero (pestaña "Cambiar Fondo" o "Imagen")');
      return;
    }
    setLoading(true);
    setBgResult(null);
    const loadingToast = toast.loading('IA aislando al sujeto… 20-50 segundos', { duration: Infinity });
    try {
      const formData = new FormData();
      formData.append('image', imageToSend);
      formData.append('prompt', 'Isolate only the person/baby/animal subject');
      formData.append('style', style);
      formData.append('quality', bgQuality);
      formData.append('mode', 'isolate_subject');

      const submitResp = await fetch(`${API_URL}/api/arts/modify-async`, { method: 'POST', body: formData });
      const submitText = await submitResp.text();
      let submitData = null;
      try { submitData = submitText ? JSON.parse(submitText) : null; } catch (_e) { submitData = null; }
      if (!submitResp.ok || !submitData?.job_id) {
        toast.dismiss(loadingToast);
        toast.error(submitData?.detail || 'No se pudo iniciar el procesamiento');
        return;
      }
      const jobId = submitData.job_id;

      let finalData = null;
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const poll = await fetch(`${API_URL}/api/arts/job/${jobId}`);
          const pollText = await poll.text();
          let pollData = null;
          try { pollData = pollText ? JSON.parse(pollText) : null; } catch (_e) { pollData = null; }
          if (pollData?.status === 'done') { finalData = pollData.result; break; }
          if (pollData?.status === 'error') {
            toast.dismiss(loadingToast);
            toast.error(pollData.error || 'La IA no pudo aislar al sujeto');
            return;
          }
        } catch (_e) { /* keep polling */ }
      }

      toast.dismiss(loadingToast);

      if (finalData && finalData.success) {
        setBgResult(finalData.image_base64);
        setFileId(finalData.file_id);
        toast.success('¡Solo el sujeto! Objetos eliminados con Gemini');
      } else {
        toast.error('La IA tardó demasiado. Intenta de nuevo.', { duration: 8000 });
      }
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Error: ' + (error.message || 'desconocido'));
    } finally {
      setLoading(false);
    }
  };

  // Download the bg result
  const downloadBgResult = () => {
    if (!bgResult) return;
    const link = document.createElement('a');
    link.href = bgResult;
    link.download = `fondo-reemplazado-${Date.now()}.png`;
    link.click();
  };

  // Send the bg result as a WhatsApp sticker (WebP 512x512, <100KB)
  const sendAsWhatsAppSticker = async () => {
    if (!bgResult) return;
    try {
      toast.loading('Convirtiendo a sticker…', { id: 'sticker-conv' });
      const file = await buildWhatsAppSticker(bgResult);
      const sizeKB = (file.size / 1024).toFixed(0);
      const result = await shareStickerOrDownload(file, 'Sticker PaliStore');
      toast.dismiss('sticker-conv');
      if (result === 'shared') {
        toast.success(`Sticker enviado (${sizeKB} KB)`);
      } else {
        toast.success(`Sticker descargado (${sizeKB} KB). Compártelo en WhatsApp adjuntándolo.`);
      }
    } catch (err) {
      toast.dismiss('sticker-conv');
      toast.error('Error al crear sticker: ' + (err.message || 'desconocido'));
    }
  };

  // ---- GIF Animado ----
  const handleGenerateGif = async () => {
    setGifGenerating(true);
    setGifProgress(0);
    if (gifResult) {
      URL.revokeObjectURL(gifResult);
      setGifResult(null);
    }
    try {
      // Pick the base image from any other tab (bg result preferred → originalImage → backgroundImage)
      let baseImage = bgResult || originalImage || backgroundImage || null;
      // These effects work without a base image (background is generated)
      const noImageEffects = ['color-bg', 'solid-bg'];
      if (!baseImage && !noImageEffects.includes(gifEffect)) {
        toast.error('Sube o genera una imagen primero (o usa "Fondo de color fijo" o "Fondo de colores animado")');
        setGifGenerating(false);
        return;
      }
      if (gifEffect === 'text' && !gifText.trim()) {
        toast.error('Escribe el texto para animar');
        setGifGenerating(false);
        return;
      }

      // Sticker mode: auto-remove background via backend (Remove.bg → Gemini fallback)
      if (gifSticker && baseImage) {
        toast.loading('Quitando fondo automáticamente…', { id: 'gif-rm' });
        try {
          // Convert dataURL to File for upload
          const res = await fetch(baseImage);
          const blob = await res.blob();
          const file = new File([blob], 'gif-source.png', { type: blob.type || 'image/png' });

          const upFd = new FormData();
          upFd.append('file', file);
          const upResp = await fetch(`${API_URL}/api/sublimation/upload`, { method: 'POST', body: upFd });
          const upData = await upResp.json();
          if (!upResp.ok || !upData.file_id) throw new Error(upData.detail || 'Error al subir');

          const rbFd = new FormData();
          rbFd.append('file_id', upData.file_id);
          const rbResp = await fetch(`${API_URL}/api/sublimation/remove-background`, { method: 'POST', body: rbFd });
          const rbData = await rbResp.json();
          toast.dismiss('gif-rm');
          if (!rbResp.ok || !rbData.preview) {
            toast.error(rbData.detail || 'No se pudo quitar el fondo. Sigo sin transparencia.');
          } else {
            baseImage = rbData.preview;
            if (rbData.fallback === 'gemini') {
              toast.success('Fondo quitado con Gemini');
            }
          }
        } catch (e) {
          toast.dismiss('gif-rm');
          toast.error('Quitar fondo falló: ' + (e.message || 'error'));
          // Continue with original image but force sticker off
        }
      }

      const blob = await buildGif({
        imageSrc: baseImage,
        effect: gifEffect,
        text: gifText.trim(),
        textColor: gifTextColor,
        strokeColor: gifStrokeColor,
        bgColor: gifBgColor,
        size: gifSize,
        frames: gifFrames,
        fps: 12,
        stickerMode: gifSticker,
        onProgress: (p) => setGifProgress(Math.round(p * 100)),
      });

      const url = URL.createObjectURL(blob);
      setGifResult(url);
      toast.success(`¡GIF creado! (${(blob.size / 1024).toFixed(0)} KB)`);
    } catch (err) {
      toast.error('Error al crear GIF: ' + (err.message || 'desconocido'));
    } finally {
      setGifGenerating(false);
      setGifProgress(0);
    }
  };

  const downloadGif = () => {
    if (!gifResult) return;
    const link = document.createElement('a');
    link.href = gifResult;
    link.download = `palistore-gif-${Date.now()}.gif`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const shareGifWhatsApp = async () => {
    if (!gifResult) return;
    try {
      const blob = await fetch(gifResult).then(r => r.blob());
      const file = new File([blob], `palistore-${Date.now()}.gif`, { type: 'image/gif' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'GIF PaliStore' });
      } else {
        // Fallback: download then open whatsapp
        downloadGif();
        toast.info('GIF descargado. Adjúntalo manualmente en WhatsApp.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') toast.error('No se pudo compartir');
    }
  };

  // Generate AI text
  const generateAIText = async () => {
    if (!prompt.trim()) {
      toast.error('Escribe qué tipo de texto quieres');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/arts/generate-text`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, style }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const newLayer = {
          id: Date.now(),
          text: data.text,
          font: data.suggested_font || 'Pacifico',
          size: 56,
          color: data.suggested_color || '#FFFFFF',
          strokeColor: '#000000',
          strokeWidth: 3,
          x: 50,
          y: 50,
          bold: false,
          italic: false,
          align: 'center',
          shadow: true,
          maxWidth: 80
        };
        setTextLayers([...textLayers, newLayer]);
        setSelectedLayerId(newLayer.id);
        toast.success('¡Texto generado! Ajusta la posición con los controles.');
      } else {
        toast.error(data.detail || 'Error al generar texto');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // Draw text with word wrap
  const drawTextWithWrap = (ctx, text, x, y, maxWidth, lineHeight, layer) => {
    const words = text.split(' ');
    let line = '';
    let currentY = y;
    const lines = [];
    
    // Calculate lines
    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + ' ';
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;
      
      if (testWidth > maxWidth && n > 0) {
        lines.push(line.trim());
        line = words[n] + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line.trim());
    
    // Adjust Y to center all lines
    const totalHeight = lines.length * lineHeight;
    currentY = y - (totalHeight / 2) + (lineHeight / 2);
    
    // Draw each line
    lines.forEach((line, index) => {
      const lineY = currentY + (index * lineHeight);
      
      // Stroke
      if (layer.strokeWidth > 0) {
        ctx.strokeStyle = layer.strokeColor;
        ctx.lineWidth = layer.strokeWidth * 2;
        ctx.lineJoin = 'round';
        ctx.strokeText(line, x, lineY);
      }
      
      // Fill
      ctx.fillStyle = layer.color;
      ctx.fillText(line, x, lineY);
    });
  };

  // Render canvas preview
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const { width, height } = canvasSize;
    
    canvas.width = width;
    canvas.height = height;

    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    const drawContent = () => {
      // Draw text layers on top
      textLayers.forEach(layer => {
        const x = (layer.x / 100) * width;
        const y = (layer.y / 100) * height;
        const maxWidth = (layer.maxWidth / 100) * width;

        // Set font
        let fontStyle = '';
        if (layer.italic) fontStyle += 'italic ';
        if (layer.bold) fontStyle += 'bold ';
        ctx.font = `${fontStyle}${layer.size}px "${layer.font}"`;
        ctx.textAlign = layer.align;
        ctx.textBaseline = 'middle';

        // Shadow
        if (layer.shadow) {
          ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
          ctx.shadowBlur = 8;
          ctx.shadowOffsetX = 4;
          ctx.shadowOffsetY = 4;
        }

        // Draw text with word wrap
        const lineHeight = layer.size * 1.2;
        drawTextWithWrap(ctx, layer.text, x, y, maxWidth, lineHeight, layer);

        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;

        // Draw selection indicator if selected
        if (layer.id === selectedLayerId) {
          const metrics = ctx.measureText(layer.text);
          const textWidth = Math.min(metrics.width, maxWidth);
          ctx.strokeStyle = '#00ff00';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(
            x - textWidth / 2 - 10, 
            y - layer.size / 2 - 10, 
            textWidth + 20, 
            layer.size + 20
          );
          ctx.setLineDash([]);
        }
      });
    };

    // Draw background image if exists
    if (backgroundImage) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Use CONTAIN mode to show full image without cropping
        // Calculate scale to fit image completely within canvas
        const scaleX = width / img.width;
        const scaleY = height / img.height;
        
        let scale, imgX, imgY, drawWidth, drawHeight;
        
        if (fitMode === 'contain') {
          // Contain: show entire image, may have letterboxing
          scale = Math.min(scaleX, scaleY);
          drawWidth = img.width * scale;
          drawHeight = img.height * scale;
          imgX = (width - drawWidth) / 2;
          imgY = (height - drawHeight) / 2;
          
          // Fill background with dark color for letterboxing
          ctx.fillStyle = '#1a1a2e';
          ctx.fillRect(0, 0, width, height);
        } else {
          // Cover: fill canvas, may crop image
          scale = Math.max(scaleX, scaleY);
          drawWidth = img.width * scale;
          drawHeight = img.height * scale;
          imgX = (width - drawWidth) / 2;
          imgY = (height - drawHeight) / 2;
        }
        
        ctx.drawImage(img, imgX, imgY, drawWidth, drawHeight);
        drawContent();
      };
      img.src = backgroundImage;
    } else {
      // Draw gradient background
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#667eea');
      gradient.addColorStop(1, '#764ba2');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      drawContent();
    }
  }, [backgroundImage, textLayers, selectedLayerId, canvasSize, fitMode]);

  // Update canvas when dependencies change
  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  // Download final image
  const downloadImage = () => {
    // Deselect to hide selection box
    const prevSelected = selectedLayerId;
    setSelectedLayerId(null);
    
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const link = document.createElement('a');
      
      // Generate filename with size info
      let filename = 'arte';
      if (aspectRatio === 'sublimacion') {
        const paperLabel = paperSizes.find(p => p.value === paperSize)?.label.split(' ')[1] || 'custom';
        filename = `sublimacion_${paperLabel}_${customSizeCm.width}x${customSizeCm.height}cm_${printDpi}dpi`;
      } else {
        filename = `arte_${canvasSize.width}x${canvasSize.height}`;
      }
      link.download = `${filename}_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
      
      // Show size info
      if (aspectRatio === 'sublimacion') {
        const paperLabel = paperSizes.find(p => p.value === paperSize)?.label || 'Personalizado';
        toast.success(`Imagen descargada: ${paperLabel} - ${customSizeCm.width}×${customSizeCm.height} cm (${canvasSize.width}×${canvasSize.height} px a ${printDpi} DPI)`);
      } else {
        toast.success(`Imagen descargada: ${canvasSize.width}×${canvasSize.height} px`);
      }
      
      // Restore selection
      setSelectedLayerId(prevSelected);
    }, 100);
  };

  // Reset all
  const resetAll = () => {
    setBackgroundImage(null);
    setBackgroundFile(null);
    setOriginalImage(null);
    setTextLayers([]);
    setSelectedLayerId(null);
    setPrompt('');
  };

  // Restore original image
  const restoreOriginal = () => {
    if (originalImage) {
      setBackgroundImage(originalImage);
      toast.success('Imagen original restaurada');
    }
  };

  const selectedLayer = textLayers.find(l => l.id === selectedLayerId);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
            <Wand2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Editor de Artes</h1>
            <p className="text-sm text-gray-500">Diseños para WhatsApp, redes e impresiones</p>
          </div>
        </div>
        <div className="flex gap-2">
          {backgroundImage && (
            <Button variant="outline" size="sm" onClick={addTextLayer}>
              <Plus className="h-4 w-4 mr-1" />
              Agregar Texto
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Panel - Preview */}
        <div className="lg:col-span-2 order-2 lg:order-1">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-4 w-4 text-pink-500" />
                  Vista Previa
                </CardTitle>
                <div className="flex gap-2 flex-wrap">
                  {/* Aspect Ratio Selector */}
                  <Select value={aspectRatio} onValueChange={handleAspectRatioChange}>
                    <SelectTrigger className="h-8 w-36 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {aspectRatios.map((ar) => (
                        <SelectItem key={ar.value} value={ar.value}>{ar.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {/* Sublimation Size Controls */}
                  {aspectRatio === 'sublimacion' && (
                    <div className="flex items-center gap-1 flex-wrap">
                      {/* Paper size selector */}
                      <Select value={paperSize} onValueChange={handlePaperSizeChange}>
                        <SelectTrigger className="h-8 w-44 text-xs">
                          <SelectValue placeholder="Tamaño de hoja" />
                        </SelectTrigger>
                        <SelectContent>
                          {paperSizes.map((ps) => (
                            <SelectItem key={ps.value} value={ps.value}>{ps.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Custom size inputs */}
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        step="0.5"
                        value={customSizeCm.width}
                        onChange={(e) => handleSublimationSizeChange('width', e.target.value)}
                        className="h-8 w-14 text-xs"
                        title="Ancho en cm"
                      />
                      <span className="text-xs text-gray-500">×</span>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        step="0.5"
                        value={customSizeCm.height}
                        onChange={(e) => handleSublimationSizeChange('height', e.target.value)}
                        className="h-8 w-14 text-xs"
                        title="Alto en cm"
                      />
                      <span className="text-xs text-gray-500">cm</span>
                      
                      {/* DPI selector */}
                      <Select value={printDpi.toString()} onValueChange={handleDpiChange}>
                        <SelectTrigger className="h-8 w-20 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="150">150 DPI</SelectItem>
                          <SelectItem value="200">200 DPI</SelectItem>
                          <SelectItem value="300">300 DPI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Fit Mode Toggle */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setFitMode(fitMode === 'contain' ? 'cover' : 'contain')}
                    title={fitMode === 'contain' ? 'Mostrar imagen completa' : 'Llenar canvas'}
                  >
                    {fitMode === 'contain' ? '🖼️ Completa' : '✂️ Recortar'}
                  </Button>
                  {originalImage && backgroundImage !== originalImage && (
                    <Button variant="outline" size="sm" onClick={restoreOriginal}>
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Original
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={resetAll}>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Limpiar
                  </Button>
                  <Button
                    onClick={downloadImage}
                    size="sm"
                    className="bg-green-500 hover:bg-green-600"
                    disabled={!backgroundImage && textLayers.length === 0}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Descargar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center" ref={previewRef}>
                <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-2xl" style={{ maxWidth: '100%', width: '500px' }}>
                  {loading && (
                    <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-10 rounded-lg">
                      <div className="text-center text-white">
                        <Loader2 className="h-10 w-10 animate-spin mx-auto mb-2" />
                        <p className="font-medium">Generando...</p>
                        <p className="text-xs opacity-75">Puede tomar hasta 60 segundos</p>
                      </div>
                    </div>
                  )}
                  <canvas
                    ref={canvasRef}
                    className="w-full h-auto cursor-pointer"
                    style={{ aspectRatio: `${canvasSize.width}/${canvasSize.height}` }}
                    onClick={(e) => {
                      if (!selectedLayerId || !selectedLayer) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = ((e.clientX - rect.left) / rect.width) * 100;
                      const y = ((e.clientY - rect.top) / rect.height) * 100;
                      updateTextLayer(selectedLayerId, { x: Math.round(x), y: Math.round(y) });
                    }}
                  />
                  {selectedLayerId && (
                    <div className="absolute bottom-2 left-2 right-2 bg-black/80 text-white text-xs p-2 rounded">
                      💡 Haz clic en la imagen para mover el texto seleccionado
                    </div>
                  )}
                </div>
              </div>
              {/* Size info */}
              <div className="text-center text-xs text-gray-500 mt-2">
                {aspectRatio === 'sublimacion' ? (
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    📐 {customSizeCm.width} × {customSizeCm.height} cm ({canvasSize.width} × {canvasSize.height} px a {printDpi} DPI)
                  </span>
                ) : (
                  <span>{canvasSize.width} × {canvasSize.height} px</span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - Tools */}
        <div className="lg:col-span-1 order-1 lg:order-2 space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="templates" className="text-xs" data-testid="templates-tab">
                <LayoutTemplate className="h-3 w-3 mr-1" />
                Plantillas
              </TabsTrigger>
              <TabsTrigger value="generate" className="text-xs">
                <Wand2 className="h-3 w-3 mr-1" />
                IA
              </TabsTrigger>
              <TabsTrigger value="bgreplace" className="text-xs" data-testid="bgreplace-tab">
                <ImageIcon className="h-3 w-3 mr-1" />
                Fondo
              </TabsTrigger>
              <TabsTrigger value="image" className="text-xs">
                <ImageIcon className="h-3 w-3 mr-1" />
                Imagen
              </TabsTrigger>
              <TabsTrigger value="text" className="text-xs">
                <Type className="h-3 w-3 mr-1" />
                Texto
              </TabsTrigger>
              <TabsTrigger value="gif" className="text-xs" data-testid="gif-tab">
                <Sparkles className="h-3 w-3 mr-1" />
                GIF
              </TabsTrigger>
            </TabsList>

            {/* Plantillas prediseñadas Tab */}
            <TabsContent value="templates" className="space-y-4 mt-3" data-testid="templates-panel">
              <p className="text-xs text-slate-500">
                Elegí una plantilla, se carga el fondo con textos editables listos para modificar y descargar.
              </p>

              {/* Más usadas primero */}
              <div>
                <h4 className="text-sm font-bold text-slate-800 mb-2">⭐ Más usadas</h4>
                <div className="grid grid-cols-2 gap-2">
                  {ART_TEMPLATES_FLAT.slice(0, 4).map((tpl) => (
                    <button
                      key={`top-${tpl.id}`}
                      data-testid={`template-top-${tpl.id}`}
                      onClick={() => applyTemplate(tpl)}
                      className="group relative rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-500 transition-colors"
                    >
                      <img src={tpl.bg} alt={tpl.name} className="w-full h-24 object-cover" />
                      <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[11px] font-semibold px-1 py-1 text-center">
                        {tpl.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Por categoría */}
              {ART_TEMPLATE_CATEGORIES.map((cat) => (
                <div key={cat.id} data-testid={`template-category-${cat.id}`}>
                  <h4 className="text-sm font-bold text-slate-800 mb-2">{cat.name}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {cat.templates.map((tpl) => (
                      <button
                        key={tpl.id}
                        data-testid={`template-${tpl.id}`}
                        onClick={() => applyTemplate(tpl)}
                        className="group relative rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-500 transition-colors"
                      >
                        <img src={tpl.bg} alt={tpl.name} className="w-full h-24 object-cover" />
                        <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[11px] font-semibold px-1 py-1 text-center">
                          {tpl.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </TabsContent>

            {/* AI Generation Tab */}
            <TabsContent value="generate" className="space-y-3 mt-3">
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Describe tu diseño de FONDO</label>
                    <Textarea
                      placeholder="Ej: Fondo colorido con flores, luces de neón, ollas de oro brillantes..."
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      className="min-h-[80px] text-sm"
                      disabled={loading}
                    />
                    <p className="text-xs text-amber-600 mt-1 bg-amber-50 p-2 rounded">
                      ⚠️ <strong>NO incluyas texto en la descripción.</strong> El texto se agrega después como capas editables para que salga perfecto.
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">Estilo</label>
                    <Select value={style} onValueChange={setStyle} disabled={loading}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {styles.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium mb-1 block">Calidad IA</label>
                      <Select value={aiQuality} onValueChange={setAiQuality} disabled={loading}>
                        <SelectTrigger className="h-9" data-testid="ai-quality-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high">🏆 Pro (4K, mejor calidad)</SelectItem>
                          <SelectItem value="fast">⚡ Flash (más rápido)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Aspecto</label>
                      <Select value={aiAspect} onValueChange={setAiAspect} disabled={loading}>
                        <SelectTrigger className="h-9" data-testid="ai-aspect-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1:1">⬜ Cuadrado 1:1</SelectItem>
                          <SelectItem value="9:16">📱 Vertical 9:16</SelectItem>
                          <SelectItem value="16:9">🖼️ Horizontal 16:9</SelectItem>
                          <SelectItem value="4:5">📷 Social 4:5</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium mb-1 block">Variantes a generar</label>
                    <Select value={String(aiVariations)} onValueChange={(v) => setAiVariations(parseInt(v))} disabled={loading}>
                      <SelectTrigger className="h-9" data-testid="ai-variations-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 imagen</SelectItem>
                        <SelectItem value="2">2 imágenes</SelectItem>
                        <SelectItem value="3">3 imágenes</SelectItem>
                        <SelectItem value="4">4 imágenes (elegir la mejor)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={generateArt}
                    disabled={loading || prompt.length === 0}
                    className={`w-full ${prompt.length > 0 && !loading ? 'bg-gradient-to-r from-purple-500 to-pink-500' : ''}`}
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generando...</>
                    ) : (
                      <><Wand2 className="mr-2 h-4 w-4" />Generar Fondo con IA</>
                    )}
                  </Button>
                  
                  {backgroundImage && (
                    <p className="text-xs text-green-600 text-center">
                      ✅ Fondo listo. Ahora ve a la pestaña "Texto" para agregar tu mensaje.
                    </p>
                  )}

                  {variationResults.length > 1 && (
                    <div>
                      <label className="text-xs font-medium mb-1 block">Selecciona tu favorita:</label>
                      <div className="grid grid-cols-2 gap-2">
                        {variationResults.map((v, idx) => (
                          <button
                            key={v.file_id}
                            onClick={() => {
                              setBackgroundImage(v.image_base64);
                              setOriginalImage(v.image_base64);
                              setFileId(v.file_id);
                              toast.success(`Variante ${idx + 1} seleccionada`);
                            }}
                            className={`border-2 rounded-md overflow-hidden hover:border-purple-500 transition-colors ${
                              fileId === v.file_id ? 'border-purple-600 ring-2 ring-purple-300' : 'border-slate-200'
                            }`}
                            data-testid={`variation-${idx}`}
                          >
                            <img src={v.image_base64} alt={`Variante ${idx + 1}`} className="w-full h-auto block" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Reemplazar Fondo Tab - Gemini Nano Banana image-to-image */}
            <TabsContent value="bgreplace" className="space-y-3 mt-3">
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <div className="bg-purple-50 border border-purple-200 rounded p-2 text-xs text-purple-800">
                    ✨ Sube una imagen y describe el nuevo fondo. La IA mantendrá el sujeto y reemplazará el fondo.
                  </div>

                  <input
                    type="file"
                    ref={bgFileInputRef}
                    onChange={handleBgImageUpload}
                    accept="image/*"
                    className="hidden"
                    data-testid="bgreplace-file-input"
                  />

                  {!bgImagePreview ? (
                    <div className="space-y-2">
                      <Button
                        onClick={() => bgFileInputRef.current?.click()}
                        className="w-full"
                        variant="outline"
                        disabled={loading}
                        data-testid="bgreplace-upload-btn"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Subir imagen original
                      </Button>
                      {(backgroundImage || originalImage) && (
                        <div className="bg-emerald-50 border border-emerald-300 rounded-md p-2 text-xs">
                          <div className="flex items-center gap-2">
                            <img src={originalImage || backgroundImage} alt="Editor" className="h-12 w-12 object-cover rounded border" />
                            <div className="flex-1">
                              <p className="text-emerald-800 font-medium">✓ Imagen ya cargada en el editor</p>
                              <p className="text-emerald-700">Se usará automáticamente. O sube una nueva.</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative border rounded-md overflow-hidden">
                        <img src={bgImagePreview} alt="Original" className="w-full h-auto block max-h-48 object-contain bg-slate-100" />
                        <Button
                          onClick={() => {
                            setBgImageFile(null);
                            setBgImagePreview(null);
                            setBgResult(null);
                            if (bgFileInputRef.current) bgFileInputRef.current.value = '';
                          }}
                          size="sm"
                          variant="destructive"
                          className="absolute top-1 right-1 h-7 w-7 p-0"
                          data-testid="bgreplace-remove-btn"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <Button
                        onClick={() => bgFileInputRef.current?.click()}
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={loading}
                      >
                        Cambiar imagen
                      </Button>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium mb-1 block">Plantillas rápidas</label>
                    <div className="grid grid-cols-2 gap-1 max-h-44 overflow-y-auto p-1 border rounded-md bg-slate-50">
                      {bgPresets.map((p) => (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => setBgPrompt(p.prompt)}
                          disabled={loading}
                          className="text-xs text-left px-2 py-1.5 rounded bg-white border border-slate-200 hover:border-purple-400 hover:bg-purple-50 transition-colors disabled:opacity-50"
                          data-testid={`bgreplace-preset-${p.label}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">Toca una plantilla para rellenar la descripción y edítala si quieres.</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">Describe el nuevo fondo</label>
                    <Textarea
                      placeholder="Ej: Playa tropical al atardecer, cielo con nubes anaranjadas..."
                      value={bgPrompt}
                      onChange={(e) => setBgPrompt(e.target.value)}
                      className="min-h-[70px] text-sm"
                      disabled={loading}
                      data-testid="bgreplace-prompt-input"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-medium mb-1 block">Calidad IA</label>
                    <Select value={bgQuality} onValueChange={setBgQuality} disabled={loading}>
                      <SelectTrigger className="h-9" data-testid="bgreplace-quality-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="high">🏆 Pro (mejor calidad, ~30-50s)</SelectItem>
                        <SelectItem value="fast">⚡ Flash (~15-25s)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleReplaceBackground}
                    disabled={loading || (!bgImageFile && !backgroundFile && !originalImage && !backgroundImage) || !bgPrompt.trim()}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    data-testid="bgreplace-generate-btn"
                  >
                    {loading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reemplazando fondo...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" /> Reemplazar fondo con IA</>
                    )}
                  </Button>

                  <div className="border-t pt-3">
                    <Button
                      onClick={handleIsolateSubject}
                      disabled={loading || (!bgImageFile && !backgroundFile && !originalImage && !backgroundImage)}
                      className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                      variant="default"
                      data-testid="bgreplace-isolate-btn"
                    >
                      🎯 {loading ? 'Aislando…' : 'Solo persona (quitar objetos)'}
                    </Button>
                    <p className="text-[10px] text-amber-700 mt-1">
                      Elimina todos los objetos alrededor (mesa, silla, comida, etc.) y deja solo a la persona/bebé/animal con <strong>fondo transparente (PNG)</strong>, listo para usar como sticker.
                    </p>
                  </div>

                  {bgResult && (
                    <div className="space-y-2 pt-2 border-t">
                      <label className="text-xs font-medium block">Resultado:</label>
                      <div className="border-2 border-purple-300 rounded-md overflow-hidden bg-slate-100">
                        <img src={bgResult} alt="Resultado" className="w-full h-auto block max-h-64 object-contain" data-testid="bgreplace-result-image" />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Button
                          onClick={useBgResultAsBackground}
                          variant="default"
                          size="sm"
                          data-testid="bgreplace-use-btn"
                        >
                          <ImageIcon className="h-4 w-4 mr-1" />
                          Usar
                        </Button>
                        <Button
                          onClick={downloadBgResult}
                          variant="outline"
                          size="sm"
                          data-testid="bgreplace-download-btn"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Descargar
                        </Button>
                        <Button
                          onClick={sendAsWhatsAppSticker}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="bgreplace-sticker-btn"
                          title="Convertir a sticker WhatsApp (WebP 512x512)"
                        >
                          📲 Sticker
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* GIF Animado Tab */}
            <TabsContent value="gif" className="space-y-3 mt-3">
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-800">
                    ✨ Crea GIFs animados para WhatsApp, Instagram Status, Facebook. Sin costo de IA.
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">Efecto</label>
                    <Select value={gifEffect} onValueChange={setGifEffect} disabled={gifGenerating}>
                      <SelectTrigger className="h-9" data-testid="gif-effect-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">⌨️ Texto que aparece letra por letra</SelectItem>
                        <SelectItem value="solid-bg">🎨 Fondo de color fijo (recomendado para texto)</SelectItem>
                        <SelectItem value="hearts">💖 Corazones flotando</SelectItem>
                        <SelectItem value="confetti">🎊 Confeti cayendo</SelectItem>
                        <SelectItem value="sparkles">✨ Brillos / destellos</SelectItem>
                        <SelectItem value="zoom">🔍 Zoom suave (respira)</SelectItem>
                        <SelectItem value="color-bg">🌈 Fondo de colores animado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Texto {gifEffect === 'text' ? '(obligatorio)' : '(opcional)'}
                    </label>
                    <Textarea
                      value={gifText}
                      onChange={(e) => setGifText(e.target.value)}
                      placeholder={"Ej:\nPOR FAVOR\nREVISAR Y CONFIRMAR"}
                      maxLength={200}
                      disabled={gifGenerating}
                      data-testid="gif-text-input"
                      className="min-h-[70px] text-sm"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      💡 Usa Enter para saltos de línea. El tamaño se ajusta automáticamente.
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs font-medium mb-1 block">Color texto</label>
                      <Input
                        type="color"
                        value={gifTextColor}
                        onChange={(e) => setGifTextColor(e.target.value)}
                        disabled={gifGenerating}
                        className="h-9 w-full p-1"
                        data-testid="gif-text-color"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Borde texto</label>
                      <Input
                        type="color"
                        value={gifStrokeColor}
                        onChange={(e) => setGifStrokeColor(e.target.value)}
                        disabled={gifGenerating}
                        className="h-9 w-full p-1"
                        data-testid="gif-stroke-color"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Fondo</label>
                      <Input
                        type="color"
                        value={gifBgColor}
                        onChange={(e) => setGifBgColor(e.target.value)}
                        disabled={gifGenerating || gifEffect !== 'solid-bg'}
                        title={gifEffect !== 'solid-bg' ? "Solo para 'Fondo de color fijo'" : "Color del fondo"}
                        className="h-9 w-full p-1"
                        data-testid="gif-bg-color"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium mb-1 block">Tamaño</label>
                      <Select value={String(gifSize)} onValueChange={(v) => setGifSize(parseInt(v, 10))} disabled={gifGenerating}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="240">📱 Pequeño 240×240 (rápido)</SelectItem>
                          <SelectItem value="320">⭐ Mediano 320×320 (WhatsApp)</SelectItem>
                          <SelectItem value="480">📸 Grande 480×480 (Instagram)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Duración</label>
                      <Select value={String(gifFrames)} onValueChange={(v) => setGifFrames(parseInt(v, 10))} disabled={gifGenerating}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="18">~1.5s (más liviano)</SelectItem>
                          <SelectItem value="30">~2.5s (recomendado)</SelectItem>
                          <SelectItem value="48">~4s (más largo)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded p-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-amber-900">🪄 Sticker WhatsApp (fondo transparente)</p>
                      <p className="text-[10px] text-amber-700">Quita el fondo automáticamente y genera un GIF transparente.</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={gifSticker}
                      onChange={(e) => setGifSticker(e.target.checked)}
                      disabled={gifGenerating}
                      className="ml-2 w-5 h-5 accent-amber-600"
                      data-testid="gif-sticker-toggle"
                    />
                  </div>

                  <Button
                    onClick={handleGenerateGif}
                    disabled={gifGenerating}
                    className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                    data-testid="gif-generate-btn"
                  >
                    {gifGenerating ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generando GIF... {gifProgress}%</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" /> Crear GIF Animado</>
                    )}
                  </Button>

                  {gifResult && (
                    <div className="space-y-2 pt-2 border-t">
                      <label className="text-xs font-medium block">Resultado:</label>
                      <div className="border-2 border-emerald-300 rounded-md overflow-hidden bg-slate-100 flex items-center justify-center">
                        <img
                          src={gifResult}
                          alt="GIF generado"
                          className="max-w-full h-auto block"
                          style={{ maxHeight: 280 }}
                          data-testid="gif-result-image"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button onClick={shareGifWhatsApp} size="sm" data-testid="gif-share-btn">
                          <Sparkles className="h-4 w-4 mr-1" />
                          Compartir
                        </Button>
                        <Button onClick={downloadGif} variant="outline" size="sm" data-testid="gif-download-btn">
                          <Download className="h-4 w-4 mr-1" />
                          Descargar
                        </Button>
                      </div>
                      <p className="text-[10px] text-slate-500">
                        💡 En celular: el botón Compartir abre el menú nativo (WhatsApp, Instagram, etc.). En PC: descarga el archivo.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Image Tab */}
            <TabsContent value="image" className="space-y-3 mt-3">
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleBackgroundUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  <Button
                    variant="outline"
                    className="w-full h-20 border-dashed"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <div className="text-center">
                      <Upload className="h-6 w-6 mx-auto mb-1 text-gray-400" />
                      <span className="text-sm">Subir imagen de fondo</span>
                    </div>
                  </Button>

                  {backgroundImage && (
                    <div className="space-y-2">
                      <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
                        <img src={backgroundImage} alt="Fondo" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setBackgroundImage(null);
                            setBackgroundFile(null);
                            setOriginalImage(null);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Quitar
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={addTextLayer}
                        >
                          <Type className="h-4 w-4 mr-1" />
                          Agregar Texto
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Text Tab */}
            <TabsContent value="text" className="space-y-3 mt-3">
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <Button onClick={addTextLayer} className="w-full" variant="outline" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Nueva capa de texto
                  </Button>

                  {/* Text layers list */}
                  {textLayers.length > 0 && (
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {textLayers.map((layer) => (
                        <div
                          key={layer.id}
                          className={`flex items-center justify-between p-2 rounded text-sm cursor-pointer ${
                            selectedLayerId === layer.id ? 'bg-purple-100 border border-purple-300' : 'bg-gray-50 hover:bg-gray-100'
                          }`}
                          onClick={() => setSelectedLayerId(layer.id)}
                        >
                          <span className="truncate flex-1" style={{ fontFamily: layer.font }}>
                            {layer.text.substring(0, 15)}...
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteTextLayer(layer.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* AI Text Generation */}
                  <div className="pt-2 border-t">
                    <label className="text-sm font-medium mb-1 block">Generar texto con IA</label>
                    <Textarea
                      placeholder="Ej: Frase motivacional sobre el éxito..."
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      className="min-h-[50px] text-sm mb-2"
                      disabled={loading}
                    />
                    <Button
                      onClick={generateAIText}
                      disabled={loading || prompt.length === 0}
                      className="w-full"
                      size="sm"
                      variant="secondary"
                    >
                      {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                      Generar texto con IA
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Selected layer editor */}
              {selectedLayer && (
                <Card>
                  <CardHeader className="pb-2 pt-3">
                    <CardTitle className="text-sm">Editar Texto</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Input
                        value={selectedLayer.text}
                        onChange={(e) => updateTextLayer(selectedLayer.id, { text: e.target.value })}
                        className="text-sm"
                        placeholder="Escribe tu texto..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Fuente</label>
                        <Select
                          value={selectedLayer.font}
                          onValueChange={(v) => {
                            if (v) updateTextLayer(selectedLayer.id, { font: v });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Arial">Arial</SelectItem>
                            <SelectItem value="Helvetica">Helvetica</SelectItem>
                            <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                            <SelectItem value="Georgia">Georgia</SelectItem>
                            <SelectItem value="Verdana">Verdana</SelectItem>
                            <SelectItem value="Pacifico" style={{ fontFamily: 'Pacifico' }}>Pacifico (Script)</SelectItem>
                            <SelectItem value="Lobster" style={{ fontFamily: 'Lobster' }}>Lobster (Cursiva)</SelectItem>
                            <SelectItem value="Dancing Script" style={{ fontFamily: 'Dancing Script' }}>Dancing Script</SelectItem>
                            <SelectItem value="Satisfy" style={{ fontFamily: 'Satisfy' }}>Satisfy (Elegante)</SelectItem>
                            <SelectItem value="Great Vibes" style={{ fontFamily: 'Great Vibes' }}>Great Vibes</SelectItem>
                            <SelectItem value="Playfair Display" style={{ fontFamily: 'Playfair Display' }}>Playfair Display</SelectItem>
                            <SelectItem value="Oswald" style={{ fontFamily: 'Oswald' }}>Oswald (Moderna)</SelectItem>
                            <SelectItem value="Bebas Neue" style={{ fontFamily: 'Bebas Neue' }}>Bebas Neue (Bold)</SelectItem>
                            <SelectItem value="Permanent Marker" style={{ fontFamily: 'Permanent Marker' }}>Permanent Marker</SelectItem>
                            <SelectItem value="Shadows Into Light" style={{ fontFamily: 'Shadows Into Light' }}>Shadows Into Light</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Tamaño: {selectedLayer.size}</label>
                        <Slider
                          value={[selectedLayer.size]}
                          onValueChange={([v]) => updateTextLayer(selectedLayer.id, { size: v })}
                          min={20}
                          max={150}
                          step={2}
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Posición X: {selectedLayer.x}%</label>
                        <Slider
                          value={[selectedLayer.x]}
                          onValueChange={([v]) => updateTextLayer(selectedLayer.id, { x: v })}
                          min={5}
                          max={95}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">Posición Y: {selectedLayer.y}%</label>
                        <Slider
                          value={[selectedLayer.y]}
                          onValueChange={([v]) => updateTextLayer(selectedLayer.id, { y: v })}
                          min={5}
                          max={95}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Ancho máximo: {selectedLayer.maxWidth}%</label>
                      <Slider
                        value={[selectedLayer.maxWidth]}
                        onValueChange={([v]) => updateTextLayer(selectedLayer.id, { maxWidth: v })}
                        min={30}
                        max={100}
                      />
                    </div>

                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Color del texto</label>
                      <div className="flex flex-wrap gap-1">
                        {presetColors.slice(0, 10).map((color) => (
                          <button
                            key={color}
                            className={`w-6 h-6 rounded-full border-2 ${
                              selectedLayer.color === color ? 'border-purple-500 ring-2 ring-purple-300' : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                            onClick={() => updateTextLayer(selectedLayer.id, { color })}
                          />
                        ))}
                        <input
                          type="color"
                          value={selectedLayer.color}
                          onChange={(e) => updateTextLayer(selectedLayer.id, { color: e.target.value })}
                          className="w-6 h-6 rounded cursor-pointer"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Borde: {selectedLayer.strokeWidth}px</label>
                      <div className="flex gap-2 items-center">
                        <Slider
                          value={[selectedLayer.strokeWidth]}
                          onValueChange={([v]) => updateTextLayer(selectedLayer.id, { strokeWidth: v })}
                          min={0}
                          max={8}
                          className="flex-1"
                        />
                        <input
                          type="color"
                          value={selectedLayer.strokeColor}
                          onChange={(e) => updateTextLayer(selectedLayer.id, { strokeColor: e.target.value })}
                          className="w-6 h-6 rounded cursor-pointer"
                        />
                      </div>
                    </div>

                    <div className="flex gap-1 flex-wrap">
                      <Button
                        size="sm"
                        variant={selectedLayer.bold ? 'default' : 'outline'}
                        className="h-7 w-7 p-0"
                        onClick={() => updateTextLayer(selectedLayer.id, { bold: !selectedLayer.bold })}
                      >
                        <Bold className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedLayer.italic ? 'default' : 'outline'}
                        className="h-7 w-7 p-0"
                        onClick={() => updateTextLayer(selectedLayer.id, { italic: !selectedLayer.italic })}
                      >
                        <Italic className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedLayer.align === 'left' ? 'default' : 'outline'}
                        className="h-7 w-7 p-0"
                        onClick={() => updateTextLayer(selectedLayer.id, { align: 'left' })}
                      >
                        <AlignLeft className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedLayer.align === 'center' ? 'default' : 'outline'}
                        className="h-7 w-7 p-0"
                        onClick={() => updateTextLayer(selectedLayer.id, { align: 'center' })}
                      >
                        <AlignCenter className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedLayer.align === 'right' ? 'default' : 'outline'}
                        className="h-7 w-7 p-0"
                        onClick={() => updateTextLayer(selectedLayer.id, { align: 'right' })}
                      >
                        <AlignRight className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>

          {/* Quick tips */}
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-3 pb-3">
              <p className="text-xs text-purple-700">
                <strong>💡 Tip:</strong> {backgroundImage ? 'Haz clic en la imagen para posicionar el texto rápidamente' : 'Genera un fondo con IA o sube tu propia imagen para empezar'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Arts;
